/**
 * Places to Explore: the hotel as concierge. The owner lists nearby
 * attractions and the tour packages the hotel sells, and guests browse both
 * from the guest page. Configured under Manage → Settings → Services.
 *
 *   hotels/{hotelId}                                      placesToExploreService (the flag)
 *   hotels/{hotelId}/settings/placesToExplore             { enabled }
 *   hotels/{hotelId}/settings/placesToExplore/places/*        one PlaceDoc each
 *   hotels/{hotelId}/settings/placesToExplore/tourPackages/*  one TourPackageDoc each
 *
 * One document per item, like a dish, because each carries photos and is
 * edited on its own. Owner-written through callables only (`savePlace`,
 * `saveTourPackage` and friends); guests read through `getPlacesToExplore`,
 * which strips disabled items — the settings subtree is closed to them.
 *
 * **Display only.** A tour package is advertised, not ordered: nothing here
 * creates a request or a charge. `finalPrice` is computed by the server from
 * `price` and `discount` and stored beside them, so the owner's list and the
 * guest's card print the same number without either re-deriving it.
 */

export const PLACES_TO_EXPLORE_KIND = {
  PLACES: 'places',
  TOUR_PACKAGES: 'tourPackages',
} as const;

export type PlacesToExploreKind =
  (typeof PLACES_TO_EXPLORE_KIND)[keyof typeof PLACES_TO_EXPLORE_KIND];

export interface PlacesToExploreImageRef {
  path: string;
}

/* -------------------------------------------------------------------------- */
/* Places                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Distances are always kilometres. The unit is stored on each place
 * (`distanceUnit: 'km'`) so the document says what its number means, but it
 * is written by the server, never chosen: one unit means places sort by the
 * plain number and every card reads the same way.
 */
export const DISTANCE_UNIT_KM = 'km';

export type DistanceUnit = typeof DISTANCE_UNIT_KM;

export const MAX_PLACE_NAME_LENGTH = 80;
export const MAX_PLACE_LOCATION_LENGTH = 160;
export const MAX_PLACE_DESCRIPTION_LENGTH = 500;
/** Anything further is a trip, not a place near the hotel — and most likely a typo. */
export const MAX_PLACE_DISTANCE = 10_000;
export const MAX_PLACE_IMAGES = 6;
export const MAX_PLACES = 50;

/** `hotels/{hotelId}/settings/placesToExplore/places/{placeId}` */
export interface PlaceDoc {
  name: string;
  /** `name` lower-cased and whitespace-collapsed; what the uniqueness check compares. */
  nameKey: string;
  locationDetails: string;
  shortDescription?: string;
  /** Kilometres from the hotel: positive, at most two decimals. */
  distance: number;
  distanceUnit: DistanceUnit;
  /** Storage paths, never download URLs. The first one is the card image. */
  images: PlacesToExploreImageRef[];
  enabled: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/** "8 km", "2.5 km" — one formatter so the owner's list and the guest card agree. */
export function formatDistance(distance: number): string {
  const value = Number.isInteger(distance) ? String(distance) : String(Number(distance.toFixed(2)));
  return `${value} ${DISTANCE_UNIT_KM}`;
}

/* -------------------------------------------------------------------------- */
/* Tour packages                                                              */
/* -------------------------------------------------------------------------- */

export const TOUR_DISCOUNT_TYPE = {
  PERCENTAGE: 'percentage',
  AMOUNT: 'amount',
} as const;

export type TourDiscountType = (typeof TOUR_DISCOUNT_TYPE)[keyof typeof TOUR_DISCOUNT_TYPE];

export const TOUR_DISCOUNT_TYPES: readonly TourDiscountType[] = [
  TOUR_DISCOUNT_TYPE.PERCENTAGE,
  TOUR_DISCOUNT_TYPE.AMOUNT,
];

export const TOUR_DURATION_UNIT = {
  HOURS: 'hours',
  DAYS: 'days',
} as const;

export type TourDurationUnit = (typeof TOUR_DURATION_UNIT)[keyof typeof TOUR_DURATION_UNIT];

export const TOUR_DURATION_UNITS: readonly TourDurationUnit[] = [
  TOUR_DURATION_UNIT.HOURS,
  TOUR_DURATION_UNIT.DAYS,
];

/** A tour longer than this in hours is written in days; longer in days is a holiday. */
export const MAX_TOUR_DURATION: Readonly<Record<TourDurationUnit, number>> = {
  hours: 24,
  days: 30,
};

/**
 * How long a tour lasts: "3 hours", "2.5 hours", "2 days". Optional on a
 * package. Hours take one decimal (a half hour is a real tour length), days
 * are whole — "1.5 days" is how a brochure says "a day and a morning", and
 * the owner can write that in the details.
 */
export interface TourDuration {
  value: number;
  unit: TourDurationUnit;
}

/** Why a duration is not acceptable, or null when it is. Shared by the form and the callable. */
export function tourDurationProblem(value: number, unit: TourDurationUnit): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 'The duration must be more than zero.';
  }
  if (unit === TOUR_DURATION_UNIT.DAYS) {
    if (!Number.isInteger(value)) return 'Days must be a whole number.';
  } else if (Math.abs(value * 10 - Math.round(value * 10)) >= 1e-6) {
    return 'Hours can have at most one decimal, e.g. 2.5.';
  }
  if (value > MAX_TOUR_DURATION[unit]) {
    return `A tour can last at most ${MAX_TOUR_DURATION[unit]} ${unit}.`;
  }
  return null;
}

/** "1 hour", "2.5 hours", "1 day", "3 days". */
export function formatTourDuration(duration: TourDuration): string {
  const { value, unit } = duration;
  const singular = unit === TOUR_DURATION_UNIT.HOURS ? 'hour' : 'day';
  return `${value} ${value === 1 ? singular : `${singular}s`}`;
}

export const MAX_TOUR_NAME_LENGTH = 80;
export const MAX_TOUR_DETAILS_LENGTH = 1000;
/** Same ceiling as a menu price. */
export const MAX_TOUR_PRICE = 1_000_000;
export const MAX_TOUR_IMAGES = 6;
export const MAX_TOUR_PACKAGES = 30;

/**
 * `hotels/{hotelId}/settings/placesToExplore/tourPackages/{packageId}`
 *
 * `discountType` and `discount` are both present or both absent. A discount
 * is always strictly less than the price — 1–99 % or an amount below `price` —
 * so `finalPrice` is always positive: a package reduced to nothing is a typo,
 * and a complimentary tour is not something to sell on a price card.
 */
export interface TourPackageDoc {
  name: string;
  nameKey: string;
  details: string;
  /** Absent when the owner gave none. */
  duration?: TourDuration;
  price: number;
  discountType?: TourDiscountType;
  discount?: number;
  /** `price` less the discount, rounded to the paisa. Written by the server only. */
  finalPrice: number;
  images: PlacesToExploreImageRef[];
  enabled: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * True for 8, 2.5, 1.15 — false for 1.155. Compared with a tolerance, not
 * `Math.round(v * 100) === v * 100`: in binary floating point 1.15 * 100 is
 * 114.99999999999999, so the exact form refuses ordinary two-decimal values.
 */
export function hasAtMostTwoDecimals(value: number): boolean {
  return Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;
}

/** A price: positive, finite, at most two decimals, within the ceiling. */
export function isValidTourPrice(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value > 0 &&
    value <= MAX_TOUR_PRICE &&
    hasAtMostTwoDecimals(value)
  );
}

/**
 * Why a discount is not acceptable for `price`, or null when it is. Shared so
 * the form can refuse the same things the callable does, with the same words.
 */
export function tourDiscountProblem(
  price: number,
  type: TourDiscountType,
  discount: number,
): string | null {
  if (typeof discount !== 'number' || !Number.isFinite(discount) || discount <= 0) {
    return 'The discount must be more than zero.';
  }
  if (type === TOUR_DISCOUNT_TYPE.PERCENTAGE) {
    if (!Number.isInteger(discount) || discount >= 100) {
      return 'A percentage discount must be a whole number from 1 to 99.';
    }
    return null;
  }
  if (!hasAtMostTwoDecimals(discount)) {
    return 'The discount can have at most two decimals.';
  }
  if (discount >= price) {
    return 'The discount must be less than the price.';
  }
  return null;
}

/** The price a guest pays. Assumes a discount that passed `tourDiscountProblem`. */
export function tourPackageFinalPrice(
  price: number,
  discountType?: TourDiscountType,
  discount?: number,
): number {
  if (!discountType || !discount) return price;
  if (discountType === TOUR_DISCOUNT_TYPE.PERCENTAGE) {
    return round2(price - (price * discount) / 100);
  }
  return round2(price - discount);
}

/**
 * "₹5,000" / "₹1,25,000" / "₹4,499.50" — Indian grouping, paise only when
 * there are any. Grouped by hand rather than with `toLocaleString('en-IN')`,
 * whose output depends on the Intl data each runtime (Hermes, a browser,
 * Node) happens to ship.
 */
export function formatTourPrice(value: number): string {
  const paise = Math.round(value * 100);
  const rupees = String(Math.floor(paise / 100));
  const lastThree = rupees.slice(-3);
  const rest = rupees.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${lastThree}` : lastThree;
  const fraction = paise % 100;
  return `₹${grouped}${fraction ? `.${String(fraction).padStart(2, '0')}` : ''}`;
}

/** "10% OFF" / "₹500 OFF", or null when the package has no discount. */
export function tourDiscountLabel(pkg: {
  discountType?: TourDiscountType;
  discount?: number;
}): string | null {
  if (!pkg.discountType || !pkg.discount) return null;
  return pkg.discountType === TOUR_DISCOUNT_TYPE.PERCENTAGE
    ? `${pkg.discount}% OFF`
    : `${formatTourPrice(pkg.discount)} OFF`;
}

/** Case- and space-insensitive identity, so "marina beach" is "Marina Beach". */
export const placesToExploreNameKey = (name: string) =>
  name.trim().replace(/\s+/g, ' ').toLowerCase();
